import { Component, OnDestroy, ViewChild } from '@angular/core';
import { UntypedFormGroup, Validators, UntypedFormBuilder, UntypedFormControl } from '@angular/forms';

import { ApiRoutes } from '@shared/models/configs/api.endpoints';

import { ProxyService, AlertService, ConfirmModalService, ValidationService, ModalService, AuthenticationService } from '@core/services';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-forgot-password',
  templateUrl: './forgot-password.component.html',
  styleUrls: ['./forgot-password.component.less'],
  providers: [AlertService, ValidationService]
})
export class ForgotPasswordComponent implements OnDestroy {

  @ViewChild('emailInput', { static: true }) private emailInput: any;

  form: UntypedFormGroup;
  isValidated: boolean;
  isConfirmationSent: boolean;
  isEmailExists: boolean;
  loading: boolean;
  notReady: boolean;
  isSocialAccount: boolean;
  socialAccountType: string;

  private subscriptions = new Subscription();

  constructor(
    protected builder: UntypedFormBuilder,
    private proxyService: ProxyService,
    private modalService: ModalService,
    private confirmModalService: ConfirmModalService,
    public alertService: AlertService,
    private authenticationService: AuthenticationService
  ) {
    this.isValidated = true;
    this.isConfirmationSent = false;
    this.isEmailExists = false;
    this.loading = false;
    this.notReady = true;
    this.socialAccountType = '';

    // if a user typed there user name into the sign in form first before clicking
    // on the forgot password link, a session storage variable is saved for that email.
    // User that email to prepopulate the address input in the form.
    const userEmail = sessionStorage.getItem('email') as string;
    sessionStorage.removeItem('email');
    this.form = builder.group({
      email: new UntypedFormControl(null, [Validators.required], [ValidationService.asyncEmailValidator])
    });

    this.form.valueChanges.subscribe(values => {
      if (this.form.valid) {
        this.notReady = false;
      } else {
        this.notReady = true;
      }
    });

    if (userEmail) {
      this.form.controls.email.setValue(userEmail);
      this.form.controls.email.markAsDirty();
      this.form.controls.email.markAsTouched();
    }

    this.subscriptions.add(this.confirmModalService.btnEvent.subscribe(
      (data: string) => {
        if (data === 'acctNotFoundBtn1') {
          this.emailInput.nativeElement.focus();
        } else if (data === 'signinModal') {
          this.showModal(this.modalService.getSignInModalSelector());
        } else if (data === 'notvalidatedBtn1') {
          if (this.form.dirty && this.form.valid) {
            this.authenticationService.resendConfirmation(this.form.value.email);
          }
        } else {
          this.childClosed();
        }
      }
    ));
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  async onSubmit(form) {
   await this.isExists(form);
    if (this.isSocialAccount && this.isEmailExists) {
      this.getSocialAccountType(form.email);
    } else if (this.form.valid && this.isValidated) {
      this.loading = true;
      const payload = {
        'email': form.email,
      };

      this.subscriptions.add(this.proxyService.httpPost(ApiRoutes.RESETPW, payload).subscribe(
        (success) => {
          this.showTempPassword();
        },
        (err) => {
          this.loading = false;
          if (!this.isEmailExists) {
            this.accountNotFound();
          }
        }));
    } else if (this.isEmailExists === true && this.isValidated === false) {
      this.confirmModalService.show(
        'Your account needs email validation',
        '',
        'To reset your password, you’ll need to validate your email address. Would you like to resend the validation email now?',
        'Resend Email',
        '',
        'Cancel',
        true,
        'notvalidatedBtn1'
      );
    } else {
      this.accountNotFound();
    }
  }

  async isExists(form) {
    if (this.form.dirty && this.form.valid) {

      const payload = {
        accessTokenOrEmail: form.email,
        loginType: 'local'
      };

    return await this.proxyService.httpPost(ApiRoutes.EXISTS, payload).toPromise().then(
        (success) => {
          const isUserExists = <any>success.body.valueOf();
          // used to check is this email validated
          // Getting undefined issues
          this.isValidated = isUserExists.emailVerified ? true : false;
          this.isValidated = this.authenticationService.getEmailVerificationStatus(this.isValidated);

          // used to check the user exists with this email
          this.isEmailExists = isUserExists.exists ? true : false;
          // used to check is this account is external
          this.isSocialAccount = isUserExists.external ? true : false;
        },
        (err) => {
          this.alertService.error(err.error.message);
        });
    }
  }

  accountNotFound() {
    this.confirmModalService.show(
      'Account not found',
      '',
      'Uh-oh, we don\'t see an account with that information. Please confirm you entered correctly and try again.',
      'Try Again',
      '',
      'Cancel',
      true,
      'acctNotFoundBtn1',
      '',
      'signinModal'
    );
  }

  showTempPassword() {
    this.loading = false;
    this.showModal('app-temporary-password');
    sessionStorage.setItem('userEmail', this.form.value.email);
  }

  showModal(name: string) {
    this.modalService.setModalSelector(name);
    this.modalService.showModal();
  }

  childClosed() {
    this.modalService.hideModal();
  }

  getSocialAccountType(email: string) {

    const payload = {
      'email': email,
    };

    this.subscriptions.add(this.proxyService.httpPost(ApiRoutes.RESETPW, payload).subscribe(
      (success) => {
        this.loading = false;
      },
      (err) => {

          switch (err.error.code) {
            case 4016:
              this.socialAccountType = 'Google';
              break;
            case 4015:
              this.socialAccountType = 'Facebook';
              break;
            case 4018:
              this.socialAccountType  = 'Apple';
              break;
          }

          this.confirmModalService.show(
            'No password currently exists',
            '',
            'You\'ve used ' + this.socialAccountType + ' in the past to sign in. Please continue using ' + this.socialAccountType + '.',
            'OK, Got It',
            '',
            '',
            true,
            'signinModal'
          );

      }));


  }
}
